import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'; // Importing Recharts components

// Main App component
const App = () => {
  // State variables for user inputs
  const [departurePort, setDeparturePort] = useState('');
  const [arrivalPort, setArrivalPort] = useState('');
  const [numPhysicalContainers, setNumPhysicalContainers] = useState('');
  const [containerType, setContainerType] = useState('20ft');
  const [vesselType, setVesselType] = useState('Standard Container Vessel'); // Renamed from shipType
  const [fuelType, setFuelType] = useState('Heavy Fuel Oil (HFO)');
  const [avgCargoWeightPerContainer, setAvgCargoWeightPerContainer] = useState(''); // in tonnes
  const [loadFactor, setLoadFactor] = useState(''); // New state for load factor (percentage)
  const [transportMode, setTransportMode] = useState('Sea'); // New state for transport mode
  const [carrierType, setCarrierType] = useState('Generic'); // New state for carrier type

  // State variables for calculation results and UI feedback
  const [co2Emissions, setCo2Emissions] = useState(null); // Stores CO2 in kg
  const [co2Suggestions, setCo2Suggestions] = useState(null); // State for suggestions
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [distanceKm, setDistanceKm] = useState(null); // To display the estimated distance
  const [selectedUnit, setSelectedUnit] = useState('kg'); // State for selected unit

  // State for comparison scenarios
  const [scenarios, setScenarios] = useState([]);

  // Map related state and refs (UPDATED FOR GOOGLE MAPS)
  const mapContainerRef = useRef(null); // Reference to the map div
  const mapRef = useRef(null); // Reference to the Google Map instance
  const departureMarkerRef = useRef(null); // Reference to the departure port marker
  const arrivalMarkerRef = useRef(null);   // Reference to the arrival port marker
  const routePolylineRef = useRef(null);   // Reference to the route polyline
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false); // State to track Google Maps loading

  // Comprehensive Emission Factors by Transport Mode
  // Values are illustrative and should be replaced with actual GLEC/DEFRA/EPA data
  // Factors are kg CO2e per tonne-km (TTW and WTT components)
  const EMISSION_FACTORS = {
    'Sea': {
      'Heavy Fuel Oil (HFO)': { TTW: 0.0007, WTT: 0.0001 },
      'Marine Gas Oil (MGO)': { TTW: 0.0006, WTT: 0.0001 },
      'Liquefied Natural Gas (LNG)': { TTW: 0.0004, WTT: 0.0001 }, // Lower TTW, but WTT can include methane slip
      'Biofuel (Blended)': { TTW: 0.00015, WTT: 0.00005 }, // Significantly lower
    },
    'VesselEfficiencyModifiers': { // Modifiers applied to total (TTW+WTT) for Sea mode
      'Standard Container Vessel': 1.0,
      'Eco-friendly Vessel': 0.75,
      'Older/Less Efficient Vessel': 1.25,
    },
    'Air': { // Example for air cargo (actual EF depends on aircraft type, distance, load)
      'Cargo Aircraft': { TTW: 0.0005, WTT: 0.0001 }, // per tonne-km. illustrative
    },
    // New: Carrier-specific or Trade Lane-specific default factors (illustrative)
    'CarrierSpecific': {
      'Generic': { // Default fallback if no specific carrier is chosen
        'Sea': { TTW: 0.0007, WTT: 0.0001 }, // Using HFO default for 'Generic Sea'
        'Air': { TTW: 0.0005, WTT: 0.0001 }, // Using Cargo Aircraft default for 'Generic Air'
      },
      'Maersk (Average)': {
        'Sea': { TTW: 0.00065, WTT: 0.00009 }, // Slightly better than generic HFO
      },
      'CMA CGM (Average)': {
        'Sea': { TTW: 0.00068, WTT: 0.0001 }, // Slightly better than generic HFO
      },
      'MSC (Average)': {
        'Sea': { TTW: 0.0007, WTT: 0.00011 }, // Similar to generic HFO
      },
      'COSCO (Average)': {
        'Sea': { TTW: 0.00067, WTT: 0.000095 }, // Illustrative
      },
      'Hapag-Lloyd (Average)': {
        'Sea': { TTW: 0.00066, WTT: 0.00009 }, // Illustrative
      },
      'Ocean Network Express (Average)': {
        'Sea': { TTW: 0.00069, WTT: 0.000105 }, // Illustrative
      },
      'FedEx Cargo (Average)': {
        'Air': { TTW: 0.00048, WTT: 0.00009 }, // Slightly better than generic Air
      },
      'DHL Air (Average)': {
        'Air': { TTW: 0.00052, WTT: 0.00011 }, // Slightly worse than generic Air (illustrative)
      },
    }
  };


  // Curated list of UN/LOCODE ports for suggestions, now including coordinates
  const unlocodePorts = [
    { code: 'CNSHA', name: 'Shanghai, China', lat: 31.2304, lng: 121.4737 },
    { code: 'USLAX', name: 'Los Angeles, USA', lat: 33.7297, lng: -118.2625 },
    { code: 'NLRTM', name: 'Rotterdam, Netherlands', lat: 51.9225, lng: 4.47917 },
    { code: 'SGSIN', name: 'Singapore, Singapore', lat: 1.2833, lng: 103.8500 },
    { code: 'AEDXB', name: 'Dubai, UAE', lat: 25.2048, lng: 55.2708 },
    { code: 'USNYC', name: 'New York, USA', lat: 40.7128, lng: -74.0060 },
    { code: 'DEHAM', name: 'Hamburg, Germany', lat: 53.5511, lng: 9.9937 },
    { code: 'JPTYO', name: 'Tokyo, Japan', lat: 35.6895, lng: 139.6917 },
    { code: 'KRBUS', name: 'Busan, South Korea', lat: 35.1028, lng: 129.0739 },
    { code: 'ESALG', name: 'Algeciras, Spain', lat: 36.1382, lng: -5.4419 },
    { code: 'GBSOU', name: 'Southampton, UK', lat: 50.9097, lng: -1.4044 },
    { code: 'CAVAN', name: 'Vancouver, Canada', lat: 49.2827, lng: -123.1207 },
    { code: 'BRSSZ', name: 'Santos, Brazil', lat: -23.9608, lng: -46.3333 },
    { code: 'AUFRE', name: 'Fremantle, Australia', lat: -32.0569, lng: 115.7489 },
    { code: 'ZADUR', name: 'Durban, South Africa', lat: -29.8587, lng: 31.0218 },
    { code: 'CNHKG', name: 'Hong Kong, China', lat: 22.3193, lng: 114.1694 },
    { code: 'GBFXT', name: 'Felixstowe, UK', lat: 51.9616, lng: 1.3484 },
    { code: 'USOAK', name: 'Oakland, USA', lat: 37.7904, lng: -122.2711 },
    { code: 'BEANR', name: 'Antwerp, Belgium', lat: 51.2210, lng: 4.3997 },
    { code: 'DEBRE', name: 'Bremerhaven, Germany', lat: 53.5499, lng: 8.5833 },
    // Top ports in India (with approximate coordinates)
    { code: 'INNSA', name: 'Jawaharlal Nehru Port, India', lat: 18.9567, lng: 72.9557 },
    { code: 'INMUN', name: 'Mundra Port, India', lat: 22.7300, lng: 69.7000 },
    { code: 'INMAA', name: 'Chennai Port, India', lat: 13.0827, lng: 80.2707 },
    { code: 'INIXG', name: 'Kandla Port, India', lat: 22.9647, lng: 70.2119 },
    { code: 'INVTZ', name: 'Visakhapatnam Port, India', lat: 17.6900, lng: 83.2900 },
    { code: 'INCOK', name: 'Cochin Port, India', lat: 9.9667, lng: 76.2667 },
    { code: 'INPRT', name: 'Paradip Port, India', lat: 20.2667, lng: 86.6667 },
    { code: 'INCCU', name: 'Kolkata Port, India', lat: 22.5667, lng: 88.3667 },
    { code: 'INBOM', name: 'Mumbai Port, India', lat: 18.9400, lng: 72.8300 },
    { code: 'INTUT', name: 'V.O. Chidambaranar Port, India', lat: 8.7667, lng: 78.1667 },
    // Top ports in Middle East (with approximate coordinates)
    { code: 'AEJEA', name: 'Jebel Ali, UAE', lat: 25.0000, lng: 55.0500 },
    { code: 'SAJED', name: 'Jeddah Islamic Port, Saudi Arabia', lat: 21.4667, lng: 39.1667 },
    { code: 'OMSLL', name: 'Salalah Port, Oman', lat: 16.9667, lng: 54.0000 },
    { code: 'AEKHL', name: 'Khalifa Port, UAE', lat: 24.8100, lng: 54.6200 },
    { code: 'EGPSD', name: 'Port Said, Egypt', lat: 31.2667, lng: 32.3000 },
    { code: 'OMSOH', name: 'Sohar Port, Oman', lat: 24.3500, lng: 56.6500 },
    { code: 'QAHDQ', name: 'Hamad Port, Qatar', lat: 25.0400, lng: 51.2700 },
    { code: 'SADAM', name: 'Dammam Port, Saudi Arabia', lat: 26.4667, lng: 50.1167 },
    { code: 'AEKHF', name: 'Khor Fakkan, UAE', lat: 25.3333, lng: 56.3500 },
    { code: 'SAKAP', name: 'King Abdullah Port, Saudi Arabia', lat: 22.5667, lng: 39.0833 },
  ];

  // Top 30 Airports by Cargo Volume (with approximate coordinates)
  const airportNames = [
    { code: 'HKG', name: 'Hong Kong International Airport, Hong Kong', lat: 22.3089, lng: 113.9147 },
    { code: 'PVG', name: 'Shanghai Pudong International Airport, China', lat: 31.1434, lng: 121.8053 },
    { code: 'MEM', name: 'Memphis International Airport, USA', lat: 35.0423, lng: -89.9767 },
    { code: 'ANC', name: 'Ted Stevens Anchorage International Airport, USA', lat: 61.1744, lng: -149.9986 },
    { code: 'SDF', name: 'Louisville International Airport, USA', lat: 38.1740, lng: -85.7360 },
    { code: 'ICN', name: 'Incheon International Airport, South Korea', lat: 37.4691, lng: 126.4510 },
    { code: 'MIA', name: 'Miami International Airport, USA', lat: 25.7959, lng: -80.2870 },
    { code: 'DOH', name: 'Hamad International Airport, Qatar', lat: 25.2731, lng: 51.6082 }, // Added Doha
    { code: 'AUH', name: 'Abu Dhabi International Airport, UAE', lat: 24.4330, lng: 54.6511 }, // Added Abu Dhabi
    { code: 'JED', name: 'King Abdulaziz International Airport, Saudi Arabia', lat: 21.6798, lng: 39.1557 }, // Added Jeddah
    { code: 'CAN', name: 'Guangzhou Baiyun International Airport, China', lat: 23.1400, lng: 113.2900 },
    { code: 'TPE', name: 'Taiwan Taoyuan International Airport, Taiwan', lat: 25.0777, lng: 121.2328 },
    { code: 'LAX', name: 'Los Angeles International Airport, USA', lat: 33.9416, lng: -118.4085 },
    { code: 'NRT', name: 'Narita International Airport, Japan', lat: 35.7647, lng: 140.3863 },
    { code: 'DXB', name: 'Dubai International Airport, UAE', lat: 25.2532, lng: 55.3657 },
    { code: 'FRA', name: 'Frankfurt Airport, Germany', lat: 50.0379, lng: 8.5622 },
    { code: 'CDG', name: 'Charles de Gaulle Airport, France', lat: 49.0097, lng: 2.5479 },
    { code: 'LHR', name: 'Heathrow Airport, UK', lat: 51.4700, lng: -0.4543 },
    { code: 'AMS', name: 'Amsterdam Airport Schiphol, Netherlands', lat: 52.3086, lng: 4.7638 },
    { code: 'DEL', name: 'Indira Gandhi International Airport, India', lat: 28.5667, lng: 77.1000 }, // Delhi
    { code: 'BOM', name: 'Chhatrapati Shivaji Maharaj International Airport, India', lat: 19.0887, lng: 72.8681 }, // Mumbai
    { code: 'BLR', name: 'Kempegowda International Airport, India', lat: 13.1986, lng: 77.7066 }, // Bangalore
    { code: 'MAA', name: 'Chennai International Airport, India', lat: 12.9900, lng: 80.1600 }, // Chennai
    { code: 'HYD', name: 'Rajiv Gandhi International Airport, India', lat: 17.2300, lng: 78.4300 }, // Hyderabad
    { code: 'SIN', name: 'Singapore Changi Airport, Singapore', lat: 1.3644, lng: 103.9915 },
    { code: 'ORD', name: 'O\'Hare International Airport, USA', lat: 41.9742, lng: -87.9073 },
    { code: 'IST', name: 'Istanbul Airport, Turkey', lat: 41.2618, lng: 28.7408 },
    { code: 'JFK', name: 'John F. Kennedy International Airport, USA', lat: 40.6413, lng: -73.7781 },
    { code: 'BKK', name: 'Suvarnabhumi Airport, Thailand', lat: 13.6899, lng: 100.7501 },
    { code: 'SFO', name: 'San Francisco International Airport, USA', lat: 37.6213, lng: -122.3790 },
    { code: 'SYD', name: 'Sydney Airport, Australia', lat: 33.9461, lng: 151.1772 },
    { code: 'PEK', name: 'Beijing Capital International Airport, China', lat: 40.0799, lng: 116.6031 },
    { code: 'SZX', name: 'Shenzhen Bao\'an International Airport, China', lat: 22.6393, lng: 113.8105 },
    { code: 'KUL', name: 'Kuala Lumpur International Airport, Malaysia', lat: 2.7456, lng: 101.7072 },
    { code: 'GRU', name: 'São Paulo–Guarulhos International Airport, Brazil', lat: -23.4356, lng: -46.4731 },
  ];


  // Helper function to convert CO2 emissions to different units
  const convertCo2 = (co2Kg, unit) => {
    if (co2Kg === null) return null;

    const kg = parseFloat(co2Kg);

    switch (unit) {
      case 'kg':
        return `${kg.toFixed(2)} kg CO2e`;
      case 'tonnes':
        return `${(kg / 1000).toFixed(3)} tonnes CO2e`;
      case 'lbs':
        return `${(kg / 0.453592).toFixed(2)} lbs CO2e`;
      case 'trees':
        // Illustrative: 1 typical mature tree absorbs ~22 kg CO2 per year (EPA estimate for a young tree over 10 years)
        // This is a highly variable estimate.
        return `${(kg / 22).toFixed(1)} trees (annual absorption)`;
      case 'carMiles':
        // Illustrative: 1 mile driven by average passenger vehicle emits ~0.404 kg CO2 (EPA estimate)
        return `${(kg / 0.404).toFixed(0)} car miles`;
      default:
        return `${kg.toFixed(2)} kg CO2e`;
    }
  };

  // Effect to dynamically load Google Maps JS
  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && mapRef.current) {
      setIsGoogleMapsLoaded(true);
      return;
    }

    // Define the callback function if it doesn't exist globally
    if (!window.initGoogleMaps) {
      window.initGoogleMaps = () => {
        setIsGoogleMapsLoaded(true);
      };
    }

    // Load Google Maps JS API
    const script = document.createElement('script');
    // *** IMPORTANT: Replace 'YOUR_Maps_API_KEY' with your actual API key obtained from Google Cloud Console. ***
    // *** Ensure the 'Maps JavaScript API' is enabled for your project and billing is set up. ***
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDPFg0JRzEhgOzdDuuirTKChdXHjgZl0c4&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove the script tag and the global callback if the component unmounts
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      delete window.initGoogleMaps;
      if (mapRef.current) {
        // No direct .remove() for Google Maps. The map instance will be garbage collected
        // if its container is removed and no other references exist.
        mapRef.current = null;
      }
    };
  }, []); // Empty dependency array means this runs once on mount

  // Effect to initialize map when Google Maps API is loaded and container is ready
  useEffect(() => {
    // Ensure Google Maps is loaded, the map container ref is available, and the map hasn't been initialized yet
    if (isGoogleMapsLoaded && mapContainerRef.current && !mapRef.current) {
      const initializeMapWhenReady = () => {
        // Check if the container has actual dimensions before initializing
        if (mapContainerRef.current.offsetWidth > 0 && mapContainerRef.current.offsetHeight > 0) {
          // Initialize Google Map
          mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
            center: { lat: 20, lng: 0 }, // Centered, zoomed out to show world
            zoom: 2,
            // mapId: 'YOUR_MAP_ID_HERE', // Optional: if you have a custom styled map ID
            mapTypeControl: false, // Optionally hide map type control
            streetViewControl: false, // Optionally hide street view control
            fullscreenControl: false // Optionally hide fullscreen control
          });

          // Initialize empty marker and polyline layers
          departureMarkerRef.current = new window.google.maps.Marker({ map: mapRef.current });
          arrivalMarkerRef.current = new window.google.maps.Marker({ map: mapRef.current });
          routePolylineRef.current = new window.google.maps.Polyline({
            strokeColor: '#2563eb', // Blue color
            strokeOpacity: 0.7,
            strokeWeight: 3,
            map: mapRef.current,
            icons: [{ // For a dotted line (simulating Leaflet's dashArray) or an arrow
              icon: {
                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, // Arrow at the end
                scale: 3,
                strokeColor: '#2563eb'
              },
              offset: '100%'
            }],
            // A truly dotted line like Leaflet's dashArray is more complex in Google Maps,
            // often requiring multiple segments or custom symbols.
          });

          // Google Maps generally handles its own resizing well if the container is visible.
          // If the container becomes visible *after* initialization, you might need
          // to trigger `window.google.maps.event.trigger(mapRef.current, 'resize');`
          // after its dimensions are set.
        } else {
          // If dimensions are not ready, try again after a short delay
          setTimeout(initializeMapWhenReady, 100);
        }
      };
      initializeMapWhenReady(); // Start checking dimensions
    }
  }, [isGoogleMapsLoaded]); // Depends on Google Maps loading state

  // Effect to update map markers and polyline when ports change or calculation is done
  useEffect(() => {
    // Only proceed if Google Maps is loaded and map is initialized
    if (!mapRef.current || !window.google || !window.google.maps || !isGoogleMapsLoaded) return;

    // Clear existing markers and polyline
    departureMarkerRef.current.setMap(null); // Remove from map
    arrivalMarkerRef.current.setMap(null);   // Remove from map
    routePolylineRef.current.setPath([]);    // Clear polyline path

    // Determine which list of locations to use based on transport mode
    const currentLocations = transportMode === 'Sea' ? unlocodePorts : airportNames;

    const depLoc = currentLocations.find(loc => loc.name === departurePort);
    const arrLoc = currentLocations.find(loc => loc.name === arrivalPort);

    const bounds = new window.google.maps.LatLngBounds();
    let path = [];

    if (depLoc) {
      const depLatLng = { lat: depLoc.lat, lng: depLoc.lng };
      departureMarkerRef.current.setPosition(depLatLng);
      departureMarkerRef.current.setMap(mapRef.current); // Add back to map
      departureMarkerRef.current.setTitle(`Departure: ${depLoc.name}`); // Tooltip text
      bounds.extend(depLatLng);
      path.push(depLatLng);
    }

    if (arrLoc) {
      const arrLatLng = { lat: arrLoc.lat, lng: arrLoc.lng };
      arrivalMarkerRef.current.setPosition(arrLatLng);
      arrivalMarkerRef.current.setMap(mapRef.current); // Add back to map
      arrivalMarkerRef.current.setTitle(`Arrival: ${arrLoc.name}`); // Tooltip text
      bounds.extend(arrLatLng);
      path.push(arrLatLng);
    }

    if (path.length > 0) {
      routePolylineRef.current.setPath(path);
      if (path.length === 2) {
        // Fit map to bounds of both points
        mapRef.current.fitBounds(bounds);
      } else if (path.length === 1) { // If only one point is selected, center on it
        mapRef.current.setCenter(path[0]);
        mapRef.current.setZoom(5); // A reasonable zoom level for a single point
      }
    } else {
      // If no ports selected, reset map view to a global view
      mapRef.current.setCenter({ lat: 20, lng: 0 });
      mapRef.current.setZoom(2);
    }

  }, [departurePort, arrivalPort, isGoogleMapsLoaded, transportMode, ]); // Dependencies are the port names and Google Maps loading state

  /**
   * Simulates an API call to calculate CO2 emissions and get suggestions.
   * This function mimics a backend endpoint.
   * @param {object} params - The input parameters for the calculation.
   * @returns {Promise<object>} A promise that resolves with calculation results and suggestions.
   */
  const simulateCo2CalculationApi = async (params) => {
    return new Promise(async (resolve, reject) => {
      setTimeout(async () => { // Simulate network delay
        const {
          departurePort, arrivalPort, numPhysicalContainers,
          containerType, vesselType, fuelType,
          avgCargoWeightPerContainer, loadFactor,
          transportMode, carrierType
        } = params;

        // Input validation (can be more robust on a real backend)
        if (!departurePort || !arrivalPort || !numPhysicalContainers || !avgCargoWeightPerContainer || loadFactor === '') {
          return reject(new Error('Missing required calculation parameters.'));
        }

        const physicalContainers = parseInt(numPhysicalContainers, 10);
        const cargoWeightPerContainer = parseFloat(avgCargoWeightPerContainer);
        const parsedLoadFactor = parseFloat(loadFactor);

        // Step 1: Estimate the distance using an LLM (this would be a separate microservice/API call in a real setup)
        const locationType = transportMode === 'Sea' ? 'port' : 'airport';
        const distancePrompt = `Please provide the approximate great-circle nautical distance in kilometers between the ${locationType} of ${departurePort} and the ${locationType} of ${arrivalPort}. Respond with only the numerical distance in kilometers, without units or additional text. If you cannot determine it, respond with '0'.`;

        let chatHistoryDistance = [];
        chatHistoryDistance.push({ role: "user", parts: [{ text: distancePrompt }] });

        const payloadDistance = { contents: chatHistoryDistance };
        const apiUrl = `https://orchestrator.shipwaves.com/webhook/0df0dc3c-52a7-41e6-acfb-0642dcfcd29b`;
        let estimatedDistance = 0;
        try {
          const responseDistance = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payloadDistance)
          });

          if (!responseDistance.ok) {
            throw new Error(`API error fetching distance: ${responseDistance.statusText}`);
          }

          const resultDistance = await responseDistance.json();
          if (resultDistance.candidates && resultDistance.candidates.length > 0 &&
            resultDistance.candidates[0].content && resultDistance.candidates[0].content.parts &&
            resultDistance.candidates[0].content.parts.length > 0) {
            const text = resultDistance.candidates[0].content.parts[0].text;
            const parsedDistance = parseFloat(text.trim());
            if (!isNaN(parsedDistance) && parsedDistance > 0) {
              estimatedDistance = parsedDistance;
            } else {
              return reject(new Error(`Could not get a valid distance estimate for the given ${locationType}s.`));
            }
          } else {
            return reject(new Error(`Failed to get a distance estimate from the AI for ${locationType}s.`));
          }
        } catch (distError) {
          return reject(new Error(`Distance estimation failed: ${distError.message}`));
        }

        let ttwEmissionFactor, wttEmissionFactor;

        // IMPORTANT: Emission factors are illustrative. In a production environment,
        // these should be fetched from a regularly updated, verified database (e.g., GLEC, DEFRA).
        // This ensures compliance and accuracy.
        // Prioritize carrier-specific factors if selected and available for the transport mode
        if (carrierType !== 'Generic' && EMISSION_FACTORS.CarrierSpecific[carrierType] && EMISSION_FACTORS.CarrierSpecific[carrierType][transportMode]) {
          const carrierFactors = EMISSION_FACTORS.CarrierSpecific[carrierType][transportMode];
          ttwEmissionFactor = carrierFactors.TTW;
          wttEmissionFactor = carrierFactors.WTT;
        } else {
          // Fallback to generic factors based on transport mode and other selections
          if (transportMode === 'Sea') {
            const fuelFactors = EMISSION_FACTORS.Sea[fuelType];
            if (!fuelFactors) {
              return reject(new Error('Invalid fuel type selected for Sea transport.'));
            }
            ttwEmissionFactor = fuelFactors.TTW;
            wttEmissionFactor = fuelFactors.WTT;

            const vesselEfficiencyModifier = EMISSION_FACTORS.VesselEfficiencyModifiers[vesselType];
            if (vesselEfficiencyModifier === undefined) {
              return reject(new Error('Invalid vessel type selected for Sea transport.'));
            }
            // Apply vessel efficiency modifier to the total (TTW + WTT)
            ttwEmissionFactor = ttwEmissionFactor * vesselEfficiencyModifier;
            wttEmissionFactor = wttEmissionFactor * vesselEfficiencyModifier;

          } else if (transportMode === 'Air') {
            const aircraftFactors = EMISSION_FACTORS.Air['Cargo Aircraft']; // Currently only one generic air type
            if (!aircraftFactors) {
              return reject(new Error('Emission factors not found for Air transport.'));
            }
            ttwEmissionFactor = aircraftFactors.TTW;
            wttEmissionFactor = aircraftFactors.WTT;
          } else {
            return reject(new Error('Invalid transport mode selected.'));
          }
        }

        // Calculate total cargo weight in tonnes, adjusted by load factor
        let totalCargoWeightInTonnes;
        if (transportMode === 'Sea') {
          let totalTEU;
          if (containerType === '40ft') {
            totalTEU = physicalContainers * 2;
          } else {
            totalTEU = physicalContainers;
          }
          totalCargoWeightInTonnes = totalTEU * cargoWeightPerContainer * (parsedLoadFactor / 100);
        } else { // Air
          totalCargoWeightInTonnes = physicalContainers * cargoWeightPerContainer * (parsedLoadFactor / 100);
        }

        // Calculate total CO2 (TTW + WTT)
        const totalCo2 = estimatedDistance * totalCargoWeightInTonnes * (ttwEmissionFactor + wttEmissionFactor);
        const formattedCo2 = totalCo2.toFixed(2);

        // Step 2: Get CO2 reduction suggestions from LLM (another microservice/API call in a real setup)
        const suggestionsPrompt = `Considering a ${transportMode} shipment of ${physicalContainers} units with an average actual cargo weight of ${totalCargoWeightInTonnes.toFixed(2)} tonnes (based on ${avgCargoWeightPerContainer} tonnes per unit at ${parsedLoadFactor}% load factor), generating approximately ${formattedCo2} kg CO2, please provide 3-5 concise and actionable suggestions on how to reduce greenhouse gas emissions for this type of ${transportMode} freight. Focus on practical aspects relevant to shippers. Respond as a bulleted list, without introductory or concluding remarks.`;

        let chatHistorySuggestions = [];
        chatHistorySuggestions.push({ role: "user", parts: [{ text: suggestionsPrompt }] });

        const payloadSuggestions = { contents: chatHistorySuggestions };
        let suggestionsArray = [];
        try {
          const responseSuggestions = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payloadSuggestions)
          });

          if (!responseSuggestions.ok) {
            console.warn(`API error fetching suggestions: ${responseSuggestions.statusText}`);
            suggestionsArray = ["Unable to generate specific suggestions at this moment. Please try again later or refer to general eco-shipping practices."]; // Improved fallback message
          } else {
            const resultSuggestions = await responseSuggestions.json();
            if (resultSuggestions.candidates && resultSuggestions.candidates.length > 0 &&
              resultSuggestions.candidates[0].content && resultSuggestions.candidates[0].content.parts &&
              resultSuggestions.candidates[0].content.parts.length > 0) {
              const suggestionsText = resultSuggestions.candidates[0].content.parts[0].text;
              suggestionsArray = suggestionsText.split('\n').filter(s => s.trim() !== '' && s.trim().startsWith('*')).map(s => s.trim().substring(1).trim());
            } else {
              suggestionsArray = ["Unable to generate specific suggestions at this moment. Please try again later or refer to general eco-shipping practices."]; // Improved fallback message
            }
          }
        } catch (suggError) {
          console.warn(`Suggestions generation failed: ${suggError.message}`);
          suggestionsArray = ["Unable to generate specific suggestions at this moment. Please try again later or refer to general eco-shipping practices."]; // Improved fallback message
        }

        resolve({
          co2Emissions: formattedCo2,
          distanceKm: estimatedDistance,
          co2Suggestions: suggestionsArray
        });
      }, 1000); // Simulate 1-second network delay
    });
  };


  // Function to handle the CO2 calculation (now calls the simulated API)
  const calculateCo2 = async () => {
    // Reset previous results and errors
    setCo2Emissions(null);
    setCo2Suggestions(null); // Reset suggestions
    setError('');
    setDistanceKm(null);

    // Input validation
    if (!departurePort || !arrivalPort || !numPhysicalContainers || !avgCargoWeightPerContainer || loadFactor === '') {
      setError('Please fill in all fields: Departure/Arrival, Number of Physical Containers, Average Cargo Weight, and Load Factor.');
      return;
    }
    const physicalContainers = parseInt(numPhysicalContainers, 10);
    if (isNaN(physicalContainers) || physicalContainers <= 0) {
      setError('Number of Physical Containers must be a positive whole number.');
      return;
    }

    const cargoWeightPerContainer = parseFloat(avgCargoWeightPerContainer);
    if (isNaN(cargoWeightPerContainer) || cargoWeightPerContainer <= 0) {
      setError('Average Cargo Weight per Container must be a positive number.');
      return;
    }

    const parsedLoadFactor = parseFloat(loadFactor);
    if (isNaN(parsedLoadFactor) || parsedLoadFactor < 0 || parsedLoadFactor > 100) {
      setError('Load Factor must be a number between 0 and 100.');
      return;
    }

    setLoading(true); // Start loading indicator

    try {
      const result = await simulateCo2CalculationApi({
        departurePort,
        arrivalPort,
        numPhysicalContainers,
        containerType,
        vesselType,
        fuelType,
        avgCargoWeightPerContainer,
        loadFactor,
        transportMode,
        carrierType
      });

      setCo2Emissions(result.co2Emissions);
      setDistanceKm(result.distanceKm);
      setCo2Suggestions(result.co2Suggestions);

    } catch (err) {
      console.error('Error during CO2 calculation:', err);
      setError(`An error occurred: ${err.message}. Please try again.`);
    } finally {
      setLoading(false); // End loading indicator
    }
  };

  // Function to handle CSV export
  const handleExportCsv = () => {
    if (co2Emissions === null) {
      setError("Please calculate emissions first before exporting.");
      return;
    }

    const headers = [
      "Timestamp",
      "Transport Mode",
      "Departure Location",
      "Arrival Location",
      "Number of Physical Containers",
      "Container Type",
      "Average Cargo Weight (tonnes/container)",
      "Load Factor (%)",
      "Vessel/Aircraft Type",
      "Fuel Type",
      "Carrier Type", // New header
      "Estimated Distance (km)",
      "CO2 Emissions (kg CO2e)",
      "Suggestions"
    ];

    // Format suggestions for CSV
    const formattedSuggestions = co2Suggestions ? co2Suggestions.join('; ') : 'N/A';

    const data = [
      new Date().toLocaleString(),
      transportMode,
      departurePort,
      arrivalPort,
      numPhysicalContainers,
      transportMode === 'Sea' ? containerType : 'N/A', // Only include container type for Sea
      avgCargoWeightPerContainer,
      loadFactor,
      transportMode === 'Sea' ? vesselType : 'Cargo Aircraft', // Use vesselType for Sea, default for Air
      transportMode === 'Sea' ? fuelType : 'N/A', // Only include fuel type for Sea
      carrierType, // New data point
      distanceKm ? distanceKm.toFixed(2) : 'N/A',
      co2Emissions,
      `"${formattedSuggestions}"` // Enclose in quotes to handle semicolons in suggestions
    ];

    const csvContent = [
      headers.join(','),
      data.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) { // Feature detection for download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `co2_emission_report_${Date.now()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up the URL object
    } else {
      alert('Your browser does not support downloading files directly. Please copy the data manually.');
      console.log(csvContent); // Log to console as a fallback
    }
  };

  // Function to add current calculation to scenarios
  const addToComparison = () => {
    if (co2Emissions === null) {
      setError("Please perform a calculation first before adding to comparison.");
      return;
    }

    const newScenario = {
      id: Date.now(), // Unique ID for key prop
      transportMode,
      departurePort,
      arrivalPort,
      numPhysicalContainers,
      containerType: transportMode === 'Sea' ? containerType : 'N/A', // Save container type only for Sea
      avgCargoWeightPerContainer,
      loadFactor,
      vesselType: transportMode === 'Sea' ? vesselType : 'N/A', // Save vessel type only for Sea
      aircraftType: transportMode === 'Air' ? 'Cargo Aircraft' : 'N/A', // Save aircraft type for Air
      fuelType: transportMode === 'Sea' ? fuelType : 'N/A', // Save fuel type only for Sea
      carrierType, // New: Save carrier type
      distanceKm,
      co2Emissions: parseFloat(co2Emissions), // Ensure it's a number for charting
      timestamp: new Date().toLocaleString()
    };
    setScenarios(prevScenarios => [...prevScenarios, newScenario]);
    setError(''); // Clear any previous errors
  };

  // Function to clear all scenarios
  const clearComparisons = () => {
    setScenarios([]);
  };

  // Prepare data for the chart
  const chartData = scenarios.map((s, index) => ({
    name: `Scenario ${index + 1}`,
    'CO2 Emissions (kg)': s.co2Emissions,
    details: `${s.transportMode}: ${s.departurePort.split(',')[0]} to ${s.arrivalPort.split(',')[0]} - ${s.numPhysicalContainers}x${s.transportMode === 'Sea' ? s.containerType : 'units'} (${s.carrierType !== 'Generic' ? s.carrierType : (s.transportMode === 'Sea' ? s.fuelType : s.aircraftType)})`
  }));


  return (
    // Added 'font-sans' class for a generic sans-serif font
    <>
      <header class="bg-white">
        <nav class="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8" aria-label="Global">
          <div class="flex lg:flex-1">
            <a href="#" class="-m-1.5 p-1.5">
              <img class="h-8 w-auto" src="https://www.shipwaves.com/img/shipwaves/shipwaves_logo.svg" alt="" />
            </a>
          </div>
        </nav>
      </header>
      <div className="min-h-screen bg-gray-50 flex flex-col items-center p-4 sm:p-6 font-sans text-gray-800">

        {/* Header Section - Now without Shipwaves branding */}
        <header className="w-full max-w-5xl bg-white p-4 sm:p-6 rounded-b-2xl shadow-md mb-8 flex items-center justify-center border-b-4 border-blue-600">
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-700">
            CO2 Emission Calculator
          </h2>
        </header>

        {/* Hero Section - Adjusted styling for Shipwaves theme */}
        <section className="w-full max-w-5xl text-center py-12 px-6 bg-blue-600 text-white rounded-2xl shadow-xl mb-12 transform transition-all duration-300">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 leading-tight">
            Navigate Towards a Greener Future
          </h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto font-light opacity-90">
            Estimate the CO2 emissions of your container shipments and understand the environmental impact of global trade.
          </p>
        </section>

        {/* Calculator Section - Adjusted styling for Shipwaves theme */}
        <section id="calculator-section" className="w-full max-w-2xl bg-white p-8 sm:p-10 rounded-2xl shadow-2xl mb-12 border border-gray-200">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-8">
            Calculate Your Emissions
          </h2>

          {/* Input fields arranged in a grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
            <div>
              <label htmlFor="transportMode" className="block text-gray-700 text-base font-semibold mb-2">
                Transport Mode:
              </label>
              <select
                id="transportMode"
                className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                value={transportMode}
                onChange={(e) => {
                  setTransportMode(e.target.value);
                  // Clear port/airport selections when mode changes
                  setDeparturePort('');
                  setArrivalPort('');
                  // Reset carrier type to Generic when mode changes
                  setCarrierType('Generic');
                }}
              >
                <option value="Sea">Sea Freight</option>
                <option value="Air">Air Freight</option>
              </select>
            </div>

            <div>
              <label htmlFor="departurePort" className="block text-gray-700 text-base font-semibold mb-2">
                Departure {transportMode === 'Sea' ? 'Port' : 'Airport'}:
              </label>
              <input
                type="text"
                id="departurePort"
                className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out placeholder-gray-400"
                value={departurePort}
                onChange={(e) => setDeparturePort(e.target.value)}
                placeholder={transportMode === 'Sea' ? 'e.g., Shanghai, China' : 'e.g., Hong Kong Int. Airport'}
                list={transportMode === 'Sea' ? 'ports' : 'airports'}
              />
              <datalist id="ports">
                {unlocodePorts.map((port) => (
                  <option key={port.code} value={port.name} />
                ))}
              </datalist>
              <datalist id="airports">
                {airportNames.map((airport) => (
                  <option key={airport.code} value={airport.name} />
                ))}
              </datalist>
            </div>

            <div>
              <label htmlFor="arrivalPort" className="block text-gray-700 text-base font-semibold mb-2">
                Arrival {transportMode === 'Sea' ? 'Port' : 'Airport'}:
              </label>
              <input
                type="text"
                id="arrivalPort"
                className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out placeholder-gray-400"
                value={arrivalPort}
                onChange={(e) => setArrivalPort(e.target.value)}
                placeholder={transportMode === 'Sea' ? 'e.g., Los Angeles, USA' : 'e.g., Memphis Int. Airport'}
                list={transportMode === 'Sea' ? 'ports' : 'airports'}
              />
            </div>

            <div>
              <label htmlFor="numPhysicalContainers" className="block text-gray-700 text-base font-semibold mb-2">
                Number of Physical {transportMode === 'Sea' ? 'Containers' : 'Units/Packages'}:
              </label>
              <input
                type="number"
                id="numPhysicalContainers"
                className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out placeholder-gray-400"
                value={numPhysicalContainers}
                onChange={(e) => setNumPhysicalContainers(e.target.value)}
                placeholder="e.g., 100"
                min="1"
              />
            </div>

            <div>
              <label htmlFor="avgCargoWeightPerContainer" className="block text-gray-700 text-base font-semibold mb-2">
                Avg. Cargo Weight (tonnes):
              </label>
              <input
                type="number"
                id="avgCargoWeightPerContainer"
                className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out placeholder-gray-400"
                value={avgCargoWeightPerContainer}
                onChange={(e) => setAvgCargoWeightPerContainer(e.target.value)}
                placeholder="e.g., 10 (for 20ft) or 20 (for 40ft)"
                min="0.1"
                step="0.1"
              />
            </div>

            {transportMode === 'Sea' && (
              <div>
                <label htmlFor="containerType" className="block text-gray-700 text-base font-semibold mb-2">
                  Container Type:
                </label>
                <select
                  id="containerType"
                  className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                  value={containerType}
                  onChange={(e) => setContainerType(e.target.value)}
                >
                  <option value="20ft">20 ft (1 TEU)</option>
                  <option value="40ft">40 ft (2 TEU)</option>
                </select>
              </div>
            )}

            {/* New: Carrier Type selection */}
            <div>
              <label htmlFor="carrierType" className="block text-gray-700 text-base font-semibold mb-2">
                Carrier Type:
              </label>
              <select
                id="carrierType"
                className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                value={carrierType}
                onChange={(e) => setCarrierType(e.target.value)}
              >
                <option value="Generic">Generic (Default)</option>
                {/* Filter carriers based on transport mode */}
                {Object.keys(EMISSION_FACTORS.CarrierSpecific)
                  .filter(carrier => carrier !== 'Generic' && EMISSION_FACTORS.CarrierSpecific[carrier][transportMode])
                  .map(carrier => (
                    <option key={carrier} value={carrier}>{carrier}</option>
                  ))}
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Selecting a carrier provides more specific default emission factors.
              </p>
            </div>

            {/* Conditional rendering for Sea Freight specific inputs (only if carrier is Generic) */}
            {transportMode === 'Sea' && carrierType === 'Generic' && (
              <>
                <div>
                  <label htmlFor="vesselType" className="block text-gray-700 text-base font-semibold mb-2">
                    Vessel Type:
                  </label>
                  <select
                    id="vesselType"
                    className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                    value={vesselType}
                    onChange={(e) => setVesselType(e.target.value)}
                  >
                    {Object.keys(EMISSION_FACTORS.VesselEfficiencyModifiers).map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="fuelType" className="block text-gray-700 text-base font-semibold mb-2">
                    Fuel Type:
                  </label>
                  <select
                    id="fuelType"
                    className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                    value={fuelType}
                    onChange={(e) => setFuelType(e.target.value)}
                  >
                    {Object.keys(EMISSION_FACTORS.Sea).map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Placeholder for Air specific inputs (currently just using default Cargo Aircraft, only if carrier is Generic) */}
            {transportMode === 'Air' && carrierType === 'Generic' && (
              <div>
                <label htmlFor="aircraftType" className="block text-gray-700 text-base font-semibold mb-2">
                  Aircraft Type:
                </label>
                <select
                  id="aircraftType"
                  className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                  value="Cargo Aircraft" // Hardcoded for now as per EMISSION_FACTORS
                  disabled // Disable as there's only one option for now
                >
                  <option value="Cargo Aircraft">Cargo Aircraft</option>
                </select>
              </div>
            )}

            <div>
              <label htmlFor="loadFactor" className="block text-gray-700 text-base font-semibold mb-2">
                Load Factor (%):
              </label>
              <input
                type="number"
                id="loadFactor"
                className="shadow-sm appearance-none border border-gray-300 rounded-xl w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-3 focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out placeholder-gray-400"
                value={loadFactor}
                onChange={(e) => setLoadFactor(e.target.value)}
                placeholder="e.g., 75 (for 75% full)"
                min="0"
                max="100"
                step="1"
              />
            </div>
          </div>

          {/* Calculation button - Adjusted styling */}
          <button
            onClick={calculateCo2}
            disabled={loading}
            className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-opacity-75 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Calculating...
              </div>
            ) : (
              'Calculate CO2 Emissions'
            )}
          </button>

          {/* Error message display */}
          {error && (
            <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-xl shadow-md" role="alert">
              {error}
            </div>
          )}

          {/* Results display - Adjusted styling */}
          {co2Emissions !== null && (
            <div className="mt-8 p-6 bg-green-50 border border-green-300 text-green-800 rounded-2xl shadow-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                <h2 className="text-xl font-semibold mb-2 sm:mb-0">Estimated CO2 Emissions:</h2>
                <select
                  className="py-2 px-3 rounded-lg border border-green-300 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  value={selectedUnit}
                  onChange={(e) => setSelectedUnit(e.target.value)}
                >
                  <option value="kg">Kilograms (kg CO2e)</option>
                  <option value="tonnes">Metric Tons (tonnes CO2e)</option>
                  <option value="lbs">Pounds (lbs CO2e)</option>
                  <option value="trees">Trees (annual absorption)</option>
                  <option value="carMiles">Car Miles</option>
                </select>
              </div>
              <p className="text-3xl sm:text-4xl font-bold text-green-700 mb-3">
                {convertCo2(co2Emissions, selectedUnit)}
              </p>
              {distanceKm !== null && (
                <p className="text-sm text-gray-600 mt-2">
                  (Estimated distance: {distanceKm.toFixed(0)} km)
                </p>
              )}
              <p className="text-xs text-gray-500 mt-3 italic">
                Note: This calculation provides an estimated CO2e based on a combination of AI-estimated distances and illustrative emission factors (both generic and carrier-specific). While designed with principles from frameworks like GLEC (Global Logistics Emissions Council), it is a simplified model and does not constitute a full GLEC-compliant calculation, which requires more granular operational data. Actual emissions can vary significantly based on real-world operational efficiencies, specific vessel/aircraft performance, and precise route details. Equivalencies for trees and car miles are illustrative averages and should be used for general understanding only.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <button
                  onClick={addToComparison}
                  className="w-full sm:w-1/2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 transition duration-300 ease-in-out transform hover:scale-105 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                  Add to Comparison
                </button>
                <button
                  onClick={handleExportCsv}
                  className="w-full sm:w-1/2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-300 transition duration-300 ease-in-out transform hover:scale-105 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l3-3m-3 3l-3-3m-3 8h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                  Export to CSV
                </button>
              </div>
            </div>
          )}

          {/* Suggestions display - Adjusted styling */}
          {co2Suggestions && co2Suggestions.length > 0 && (
            <div className="mt-8 p-6 bg-blue-50 border border-blue-200 text-blue-800 rounded-2xl shadow-lg">
              <h2 className="text-xl font-semibold mb-4">Towards Greener Shipping:</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                {co2Suggestions.map((suggestion, index) => (
                  <li key={index}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </section>


        {/* Comparison Table Section */}
        {scenarios.length > 0 && (
          <section className="w-full max-w-5xl bg-white p-8 sm:p-10 rounded-2xl shadow-2xl mb-12 border border-gray-200">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-8">
              Comparison of Scenarios
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-xl shadow-md">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider rounded-tl-xl">#</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Mode</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Route</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Units</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Avg. Weight (t)</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Load Factor (%)</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Carrier Type</th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Generic Type Spec.</th> {/* Updated header */}
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Generic Fuel Type</th> {/* Updated header */}
                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider rounded-tr-xl">CO2e (kg)</th>
                  </tr>
                </thead>
                <tbody>
                  {scenarios.map((scenario, index) => (
                    <tr key={scenario.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors duration-200 ease-in-out border-b border-gray-200`}>
                      <td className="py-3 px-4 text-sm text-gray-800">{index + 1}</td>
                      <td className="py-3 px-4 text-sm text-gray-800">{scenario.transportMode}</td>
                      <td className="py-3 px-4 text-sm text-gray-800">{scenario.departurePort} to {scenario.arrivalPort}</td>
                      <td className="py-3 px-4 text-sm text-gray-800">
                        {scenario.numPhysicalContainers} {scenario.transportMode === 'Sea' ? `x ${scenario.containerType}` : 'units'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-800">{scenario.avgCargoWeightPerContainer}</td>
                      <td className="py-3 px-4 text-sm text-gray-800">{scenario.loadFactor}</td>
                      <td className="py-3 px-4 text-sm text-gray-800">{scenario.carrierType}</td>
                      <td className="py-3 px-4 text-sm text-gray-800">
                        {scenario.carrierType === 'Generic' ? (scenario.transportMode === 'Sea' ? scenario.vesselType : scenario.aircraftType) : 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-800">
                        {scenario.carrierType === 'Generic' && scenario.transportMode === 'Sea' ? scenario.fuelType : 'N/A'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-800">{scenario.distanceKm ? scenario.distanceKm.toFixed(0) : 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-800 font-bold">{scenario.co2Emissions.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={clearComparisons}
              className="mt-6 w-auto px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl focus:outline-none focus:ring-4 focus:ring-red-300 transition duration-300 ease-in-out transform hover:scale-105 flex items-center justify-center mx-auto"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
              Clear All Comparisons
            </button>
          </section>
        )}

        {/* Comparison Chart Section */}
        {scenarios.length > 0 && (
          <section className="w-full max-w-5xl bg-white p-8 sm:p-10 rounded-2xl shadow-2xl mb-12 border border-gray-200">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-8">
              CO2 Emissions Comparison Chart
            </h2>
            <div className="w-full h-[400px] sm:h-[500px] md:h-[600px]"> {/* Set a height for the chart */}
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{
                    top: 20, right: 30, left: 20, bottom: 50, // Increased bottom margin for X-axis labels
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="name"
                    interval={0} // Show all labels
                    angle={-45} // Rotate labels for readability
                    textAnchor="end" // Anchor text to the end of the tick
                    height={80} // Give more space for rotated labels
                    tickFormatter={(value, index) => chartData[index].details} // Use detailed label
                    style={{ fontSize: '0.8rem' }}
                  />
                  <YAxis label={{ value: 'CO2 Emissions (kg)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: '1rem' } }} />
                  <Tooltip
                    formatter={(value) => `${value.toFixed(2)} kg CO2e`}
                    labelFormatter={(label, payload) => payload[0] ? chartData[payload[0].index].details : label}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Bar dataKey="CO2 Emissions (kg)" fill="#2563eb" /> {/* Changed bar color to a deeper blue */}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-sm text-gray-600 mt-4 text-center">
              Each bar represents a scenario added to the comparison. Hover over a bar for details.
            </p>
          </section>
        )}

        {/* Map container */}
        <section className="w-full max-w-5xl bg-white p-6 sm:p-8 rounded-2xl shadow-2xl mb-12 border border-gray-200">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-8">
            Visualize Your Shipping Route
          </h2>
          <div ref={mapContainerRef} id="mapid" className="w-full rounded-xl shadow-inner border border-gray-300" style={{ height: '450px', minHeight: '300px' }}>
            {/* Google Map will be rendered here */}
          </div>
        </section>

        {/* Information Section - Adjusted styling */}
        <section className="w-full max-w-5xl bg-white p-8 sm:p-10 rounded-2xl shadow-2xl mb-12 border border-gray-200">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-8">
            Understanding Shipping Emissions
          </h2>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-lg mb-4 text-gray-700 leading-relaxed">
                Global shipping is a vital component of international trade, but it also contributes significantly to greenhouse gas emissions. Calculating and understanding these emissions is the first step towards a more sustainable logistics chain.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-3">
                <li>Maritime transport accounts for approximately <span className="font-bold text-blue-700">3% of global CO2 emissions</span>.</li>
                <li>A single large container ship can emit as much pollution as <span className="font-bold text-blue-700">50 million cars</span> in a year.</li>
                <li>The industry is working towards <span className="font-bold text-blue-700">decarbonization goals</span>, including the adoption of cleaner fuels and more efficient vessel designs.</li>
              </ul>
            </div>
            <div className="flex justify-center items-center p-4">
              <img
                src="https://www.shipwaves.com/_next/image?url=%2Fimg%2Fshipwaves%2F1.jpg&w=1080&q=75"
                alt="Container Ship Docked"
                className="rounded-xl shadow-lg w-full h-auto object-cover transform transition-transform duration-300 hover:scale-105"
                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/400x250/AEC6F3/2D3A5D?text=Container+Ship+at+Port'; }}
              />
            </div>
          </div>
        </section>

        {/* Understanding Our Calculator's Features and GLEC Alignment Section - Moved here */}
        <section className="w-full max-w-5xl bg-white p-8 sm:p-10 rounded-2xl shadow-2xl mb-12 border border-gray-200">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-8">
            Understanding Our Calculator's Features and GLEC Alignment
          </h2>
          <div className="flex flex-col gap-10">
            <div>
              <h3 className="text-2xl font-semibold text-blue-700 mb-4">Key Features of Our CO2 Calculator</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-xl shadow-md">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-blue-800 uppercase tracking-wider border-b border-gray-200">Feature</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-blue-800 uppercase tracking-wider border-b border-gray-200">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">AI-estimated distance calculation</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Provides approximate distances for sea and air routes using AI.</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Multi-modal support</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Calculates emissions for both Sea Freight and Air Freight.</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Container/Unit type flexibility</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Supports 20ft and 40ft containers for sea, and generic "units" for air.</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Cargo weight and load factor inputs</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Allows for more realistic emission estimates based on actual cargo utilization.</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Vessel and fuel type selection</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Influences emission factors for generic sea freight calculations.</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Illustrative carrier-specific factors</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Provides an option for more tailored (though illustrative) emission factors.</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Multiple unit conversions</td>
                      <td className="py-3 px-4 text-sm text-gray-700">View emissions in kilograms, metric tons, pounds, and illustrative equivalencies (trees, car miles).</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">AI-powered emission reduction suggestions</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Receive actionable advice to lower your carbon footprint.</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Scenario comparison tool</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Add and compare multiple calculation scenarios side-by-side with a bar chart.</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Data export</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Download your calculation data as a CSV file for record-keeping.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-green-700 mb-4">GLEC Compliance: What it Means for Our Calculator</h3>
              <p className="mb-6 text-gray-700">This calculator is designed with principles from the Global Logistics Emissions Council (GLEC) Framework but is <span className="font-bold text-red-700">not fully GLEC-compliant</span> for official reporting purposes.</p>

              <h4 className="text-xl font-medium text-gray-800 mb-3">Areas Aligned with GLEC Principles:</h4>
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full bg-white border border-gray-200 rounded-xl shadow-md">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-green-800 uppercase tracking-wider border-b border-gray-200">Principle</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-green-800 uppercase tracking-wider border-b border-gray-200">Explanation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Modal Differentiation</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Separates calculations for different transport modes (Sea, Air).</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Distance-Based Methodology</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Emissions are fundamentally tied to the travel distance.</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Load Factor Inclusion</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Recognizes the importance of capacity utilization in emissions.</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">TTW & WTT Components</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Incorporates both direct (Tank-to-Wheel) and upstream (Well-to-Tank) emissions.</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Cargo Weight & Unit Count</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Accounts for the volume and weight of goods transported.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h4 className="text-xl font-medium text-gray-800 mb-3">Current Limitations for Full GLEC Compliance:</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-xl shadow-md">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-red-800 uppercase tracking-wider border-b border-gray-200">Limitation</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-red-800 uppercase tracking-wider border-b border-gray-200">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Data Granularity</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Relies on illustrative/averaged emission factors and AI-estimated distances. Full GLEC compliance requires highly precise, verified operational data (e.g., specific vessel/aircraft performance, actual fuel consumption logs, detailed trade lane routing).</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Scope Limitations</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Primarily focuses on the main transport leg. Full GLEC scope often includes broader supply chain activities like first/last mile transport, warehousing, etc.</td>
                    </tr>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">No Third-Party Verification</td>
                      <td className="py-3 px-4 text-sm text-gray-700">Lacks the robust data collection, management, and independent verification processes typically required for official GLEC reporting.</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-800 font-medium">Static Emission Factors</td>
                      <td className="py-3 px-4 text-sm text-gray-700">The emission factors are static within the application. GLEC recommends regular updates from official, published sources.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-4 italic text-sm text-gray-500">
                Our tool is best used for <span className="font-bold">estimating</span>, <span className="font-bold">comparing scenarios</span>, and gaining <span className="font-bold">general insights</span> into carbon footprint, serving as a valuable first step in sustainable logistics planning.
              </p>
            </div>
          </div>
        </section>

        {/* Footer - Adjusted styling, now includes Shipwaves branding */}
        <footer className="w-full max-w-5xl text-center py-6 text-gray-600 border-t border-gray-200 mt-8">
          <div className="flex items-center justify-center space-x-3 mb-2">
            {/* Shipwaves Logo */}
            <img src="https://www.shipwaves.com/img/shipwaves/shipwaves_logo.svg" alt="Shipwaves Logo" className="h-8" />
          </div>
          <p className="text-sm">Powered by Shipwaves &copy; {(new Date()).getFullYear()}. All rights reserved.</p>
        </footer>
      </div>
    </>
  );
};

export default App;
